import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, DatePicker, Form, Input, Switch, message } from 'antd';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Tescriptions from '@/components/Tescriptions';
import BranchTransfer from '@/components/formItem/BranchTransfer';
import Editor from '@/components/formItem/Editor';
import {
  useAddAdminNotice,
  useDeleteAdminNotice,
  useUpdateAdminNotice,
} from '@/hooks/mutations/admin';
import {
  useAdminNotice,
  useAdminNoticeAudiences,
} from '@/hooks/queries/admin';
import type { AddAdminNoticeRequest } from '@/types/admin';
import type { ErrorResponse } from '@/types/api';
import { getQueryKey } from '@/utils/queryKeys';

type FormValues = AddAdminNoticeRequest & { branchIds: number[] };

const NoticeForm = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isRegister = !id;
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const query = useAdminNotice({ id: Number(id) }, { enabled: !isRegister });
  const audiencesQuery = useAdminNoticeAudiences(
    { id: Number(id) },
    { enabled: !isRegister }
  );

  const branchIds = Form.useWatch('branchIds', form);
  const isPinned = Form.useWatch('isPinned', form);
  const isGlobal = !branchIds || branchIds.length === 0;

  const handleMutationError = (error: AxiosError<ErrorResponse>) => {
    const description = error.response?.data?.description;
    if (description) {
      message.error(description);
    } else {
      message.error('요청에 실패했어요. 다시 시도해주세요');
    }
  };

  const invalidateNoticeQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getQueryKey('admin:getNoticesMain'),
    });
    queryClient.invalidateQueries({
      queryKey: getQueryKey('getPartnerNoticesMain'),
    });
  };

  const addAdminNotice = useAddAdminNotice({
    onSuccess: () => {
      invalidateNoticeQueries();
      message.success('공지사항을 성공적으로 등록했어요');
      navigate(PATH.NOTICE_MANAGE.INDEX, { replace: true });
    },
    onError: handleMutationError,
  });

  const updateAdminNotice = useUpdateAdminNotice({
    onSuccess: () => {
      message.success('공지사항을 성공적으로 수정했어요');
      navigate(PATH.NOTICE_MANAGE.INDEX);
      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getNotice', { id: Number(id) }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getPartnerNotice', { id: Number(id) }),
      });
      invalidateNoticeQueries();
    },
    onError: handleMutationError,
  });

  const deleteAdminNotice = useDeleteAdminNotice({
    onSuccess: () => {
      message.success('공지사항이 삭제되었어요');
      navigate(PATH.NOTICE_MANAGE.INDEX, { replace: true });
      invalidateNoticeQueries();
    },
    onError: handleMutationError,
  });

  if (!isRegister && !query.data) {
    return null;
  }

  const detail = isRegister ? null : query.data;

  return (
    <Form
      css={tw`flex flex-col gap-[32px]`}
      form={form}
      initialValues={
        detail
          ? {
              title: detail.title,
              contents: detail.contents,
              isPinned: detail.isPinned,
              publishedAt: detail.publishedAt
                ? dayjs(detail.publishedAt)
                : null,
              branchIds: detail.branches.map((b) => b.branchId),
            }
          : {
              title: '',
              contents: '',
              isPinned: false,
              publishedAt: null,
              branchIds: [],
            }
      }
      onFinish={(values) => {
        const publishedAt = values.publishedAt
          ? dayjs(values.publishedAt).format('YYYY-MM-DDTHH:mm:ss')
          : null;

        if (isRegister) {
          addAdminNotice.mutate({
            title: values.title,
            contents: values.contents,
            isPinned: values.isPinned,
            publishedAt,
            branchIds: values.branchIds,
          });
        } else {
          updateAdminNotice.mutate({
            id: Number(id),
            title: values.title,
            contents: values.contents,
            isPinned: values.isPinned,
            publishedAt,
            branchIds: values.branchIds,
          });
        }
      }}
      onFinishFailed={() => {
        message.error('입력 항목 확인 후 다시 등록해주세요');
      }}
      onValuesChange={(changedValues) => {
        // 지점을 선택하면 상단 고정 해제
        if (
          changedValues.branchIds &&
          changedValues.branchIds.length > 0 &&
          form.getFieldValue('isPinned')
        ) {
          form.setFieldValue('isPinned', false);
        }
        // 상단 고정을 켜면 대상 지점 초기화
        if (changedValues.isPinned === true) {
          form.setFieldValue('branchIds', []);
        }
      }}
      scrollToFirstError
    >
      <Tescriptions column={2}>
        <Tescriptions.Item label="제목" required span={2}>
          <Form.Item
            name="title"
            rules={[
              {
                required: true,
                message: '제목을 입력해주세요',
              },
            ]}
          >
            <Input showCount maxLength={255} placeholder="공지사항 제목" />
          </Form.Item>
        </Tescriptions.Item>

        <Tescriptions.Item label="내용" required span={2}>
          <Editor
            name="contents"
            required
            initialValue={detail ? detail.contents : ''}
          />
        </Tescriptions.Item>

        <Tescriptions.Item label="대상 지점" span={2}>
          <div css={tw`flex flex-col items-start gap-[8px] w-full`}>
            <Form.Item
              name="isPinned"
              valuePropName="checked"
              label="상단 고정"
              help={
                !isGlobal
                  ? '상단 고정은 전체 공지에서만 가능합니다.'
                  : undefined
              }
            >
              <Switch disabled={!isGlobal} />
            </Form.Item>

            <Form.Item name="branchIds">
              <BranchTransfer disabled={isPinned} />
            </Form.Item>
          </div>
        </Tescriptions.Item>

        <Tescriptions.Item label="게시일">
          <Form.Item name="publishedAt">
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="미입력 시 파트너에게 미노출"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Tescriptions.Item>
      </Tescriptions>

      {detail && (
        <Tescriptions column={2} title="관리 정보">
          <Tescriptions.Item label="작성자">
            {detail.creatorName}
          </Tescriptions.Item>
          <Tescriptions.Item label="작성일">
            {dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm')}
          </Tescriptions.Item>
          {detail.updaterName && (
            <>
              <Tescriptions.Item label="수정자">
                {detail.updaterName}
              </Tescriptions.Item>
              <Tescriptions.Item label="수정일">
                {detail.updatedAt
                  ? dayjs(detail.updatedAt).format('YYYY-MM-DD HH:mm')
                  : '-'}
              </Tescriptions.Item>
            </>
          )}
          {audiencesQuery.data && (
            <Tescriptions.Item label="조회 여부" span={2}>
              <div css={tw`flex flex-row flex-wrap gap-[8px] w-full`}>
                {audiencesQuery.data.map((audience) => {
                  const branchNames = audience.branches
                    .map((b) => b.branchName)
                    .join(', ');
                  return (
                    <Badge
                      key={audience.userId}
                      status={audience.viewed ? 'success' : 'default'}
                      text={
                        branchNames
                          ? `${audience.userName} (${branchNames})`
                          : audience.userName
                      }
                    />
                  );
                })}
              </div>
            </Tescriptions.Item>
          )}
        </Tescriptions>
      )}

      <div css={tw`flex justify-center items-center w-full gap-x-[8px]`}>
        {!isRegister && (
          <Button
            danger
            onClick={() => {
              if (window.confirm('정말 삭제하시겠습니까?')) {
                deleteAdminNotice.mutate({ id: Number(id) });
              }
            }}
          >
            삭제
          </Button>
        )}
        <Button onClick={() => navigate(PATH.NOTICE_MANAGE.INDEX)}>취소</Button>
        <Form.Item noStyle>
          <Button type="primary" htmlType="submit">
            {isRegister ? '등록' : '수정'}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default NoticeForm;
